import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ConfirmationCarouselProps,
  ConfirmationAuthConfirmRequest,
  ConfirmationAuthConfirmResponse,
  GetConfirmationsBatchResponse,
} from '@dreamai/types';
import api from '../../../../api';
import ConfirmationSpecialView, {
  hasSpecialView,
  getActionTitle,
} from '../../../ConfirmationSpecialView';
import { ReactComponent as HelpCircleIcon } from '../../../../icons/help-circle.svg';
import { ReactComponent as TrashIcon } from '../../../../icons/trash.svg';

import './confirmation-carousel.scss';
import SidewaysLoading from 'components/chat/sidebar/SidewaysLoading';
import ErrorState from 'components/common/ErrorState';
import InitialHereButton from './InitialHereButton';

interface ConfirmationCarouselComponentProps extends ConfirmationCarouselProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
  hideTitle?: boolean;
  inModal?: boolean;
  onClose?: () => void;
}

const ConfirmationCarousel: React.FC<ConfirmationCarouselComponentProps> = ({
  confirmationIds,
  threadId,
  updateThreadViewVisualDescription,
  hideTitle,
  inModal,
  onClose,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [confirmations, setConfirmations] = useState<
    GetConfirmationsBatchResponse['confirmations']
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState<
    'confirm' | 'cancel' | null
  >(null);

  const fetchConfirmations = async () => {
    if (!confirmationIds || confirmationIds.length === 0) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      const response = await api.post<GetConfirmationsBatchResponse>(
        '/confirmation-auth/batch',
        { confirmationIds },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        setConfirmations(response.data.confirmations);
      } else {
        setError(response.data.error || 'Failed to load confirmations');
      }
    } catch (err) {
      console.error('Error fetching confirmations:', err);
      setError('Failed to load confirmations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfirmations();
  }, [confirmationIds, getAccessTokenSilently]);

  if (loading) {
    return (
      <div className='confirmation-carousel'>
        <div className='confirmation-carousel-loading'>
          <div className='ghost'></div>

          <div className='ghost'></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='confirmation-carousel'>
        <div className='confirmation-carousel-error'>
          <ErrorState
            title='An error occured'
            subtitle='Please refresh and try again'
          />
        </div>
      </div>
    );
  }

  if (confirmations.length === 0) {
    return (
      <div className='confirmation-carousel'>
        <div className='confirmation-carousel-empty'>
          <p>No confirmations found</p>
        </div>
      </div>
    );
  }

  const handleConfirm = async (confirm: boolean) => {
    const currentConfirmation = confirmations[currentIndex];
    if (!currentConfirmation) return;

    setIsProcessing(true);
    setProcessingAction(confirm ? 'confirm' : 'cancel');

    const removeAndAdvance = () => {
      const updatedConfirmations = confirmations.filter(
        (c) => c.id !== currentConfirmation.id,
      );
      setConfirmations(updatedConfirmations);

      // Adjust index if we removed the last item
      if (currentIndex >= updatedConfirmations.length && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
    };

    // If canceling, remove immediately and fire-and-forget API call
    if (!confirm) {
      removeAndAdvance();
      setIsProcessing(false);
      setProcessingAction(null);

      // Fire and forget the API call
      const token = await getAccessTokenSilently();
      api
        .post(
          '/confirm-changes',
          {
            confirm,
            confirmationId: currentConfirmation.id,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        )
        .catch((err) => {
          console.error('Error canceling confirmation:', err);
        });

      // Close modal if in modal mode
      if (inModal) {
        onClose?.();
      }

      return;
    }

    // For confirm, wait for API response before removing
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/confirm-changes',
        {
          confirm,
          confirmationId: currentConfirmation.id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      removeAndAdvance();
    } catch (err: any) {
      setError(err.response?.data?.error || 'An error occurred');
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev < confirmations.length - 1 ? prev + 1 : prev,
    );
  };

  const currentConfirmation = confirmations[currentIndex];

  // Modal layout: different structure with Ignore at top and Confirm at bottom
  if (inModal) {
    return (
      <div className='confirmation-carousel modal-layout'>
        <div className='cc-top-wrapper'>
          <h4>Please initial</h4>
          <div className='modal-top-buttons'>
            {currentConfirmation.status === 'PENDING' && (
              <button
                onClick={() => handleConfirm(false)}
                disabled={isProcessing}
                className='ignore-button delete-request'
              >
                {isProcessing && processingAction === 'cancel' ? (
                  <div className='loading'>
                    <SidewaysLoading />
                  </div>
                ) : (
                  <>
                    <TrashIcon />
                    Delete request
                  </>
                )}
              </button>
            )}
            {onClose && (
              <button onClick={onClose} className='close-button'>
                Close
              </button>
            )}
          </div>
        </div>
        <div className='confirmation-carousel-items'>
          {currentConfirmation.status === 'PENDING' ? (
            <>
              {hasSpecialView(currentConfirmation.method_name) && (
                <div className='special-view-wrapper'>
                  <ConfirmationSpecialView
                    methodName={currentConfirmation.method_name}
                    methodInput={currentConfirmation.method_input}
                  />
                </div>
              )}
              <div className='modal-confirm-wrapper'>
                <InitialHereButton
                  onConfirm={() => handleConfirm(true)}
                  onComplete={onClose}
                />
              </div>
            </>
          ) : (
            <div className='actions-wrapper'>
              <p>
                You{' '}
                {currentConfirmation.status === 'CONFIRMED'
                  ? 'confirmed'
                  : 'cancelled'}{' '}
                at{' '}
                {currentConfirmation.actioned_at
                  ? new Date(currentConfirmation.actioned_at).toLocaleString()
                  : 'unknown time'}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default layout
  return (
    <div className='confirmation-carousel'>
      <div className='cc-top-wrapper'>
        {!hideTitle ? <h4>Confirmations</h4> : <div></div>}
        <div className='cc-navigation'>
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className='nav-button'
          >
            ←
          </button>
          <span className='page-indicator'>
            {currentIndex + 1} of {confirmations.length}
          </span>
          <button
            onClick={handleNext}
            disabled={currentIndex === confirmations.length - 1}
            className='nav-button'
          >
            →
          </button>
        </div>
      </div>
      <div className='confirmation-carousel-items'>
        <div className='confirmation-description'>
          <HelpCircleIcon />
          {currentConfirmation.task}
        </div>

        {currentConfirmation.status === 'PENDING' ? (
          <>
            <div className='actions-wrapper'>
              <button
                onClick={() => handleConfirm(false)}
                disabled={isProcessing}
                className='cancel-button'
              >
                {isProcessing && processingAction === 'cancel' ? (
                  <>
                    <div className='loading'>
                      <SidewaysLoading />
                    </div>
                  </>
                ) : (
                  'Cancel'
                )}
              </button>
              <button
                onClick={() => handleConfirm(true)}
                disabled={isProcessing}
                className='confirm-button'
              >
                {isProcessing && processingAction === 'confirm' ? (
                  <>
                    <div className='loading'>
                      <SidewaysLoading />
                    </div>
                  </>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
            {hasSpecialView(currentConfirmation.method_name) && (
              <div className='special-view-wrapper'>
                <ConfirmationSpecialView
                  methodName={currentConfirmation.method_name}
                  methodInput={currentConfirmation.method_input}
                />
              </div>
            )}
          </>
        ) : (
          <div className='actions-wrapper'>
            <p>
              You{' '}
              {currentConfirmation.status === 'CONFIRMED'
                ? 'confirmed'
                : 'cancelled'}{' '}
              at{' '}
              {currentConfirmation.actioned_at
                ? new Date(currentConfirmation.actioned_at).toLocaleString()
                : 'unknown time'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfirmationCarousel;
